import { useQuery } from '@tanstack/react-query';
import {
  type GetDocumentQueueRequestType,
  getDocumentsQueue,
  getDocumentsQueueGroups,
} from '../../api';
import { convertQueueToWorkflow } from '../helpers';
import { queryKey } from './query-key';

/**
 * Custom hook to get the document workflow by combining queue and group data.
 * This hook fetches both the document queue and its associated groups,
 * then converts the queue data into a workflow format.
 *
 * @param documentId - The ID of the document for which the workflow is to be fetched.
 * @param hasGroupsAccess - Whether the queue-groups feature is available for the
 *   current organisation. When false the groups endpoint is skipped — it 403s
 *   when gated off — and an empty group list is fed to the converter.
 */
export const useGetDocumentsWorkflow = (
  documentId?: GetDocumentQueueRequestType['documentId'],
  hasGroupsAccess = false,
) => {
  const params = { documentId: documentId! };

  return useQuery({
    queryKey: queryKey.documentWorkflow(documentId!, hasGroupsAccess),
    queryFn: async () => {
      const [queue, groups] = await Promise.all([
        getDocumentsQueue(params),
        hasGroupsAccess
          ? getDocumentsQueueGroups(params)
          : Promise.resolve({ data: [] }),
      ]);

      return (
        queue.data &&
        groups.data &&
        convertQueueToWorkflow(queue.data, groups.data)
      );
    },
    enabled: !!documentId,
  });
};
